import React, { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, Menu, MenuItem, Link as LinkTab } from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { AccountCircleIcon, LaunchIcon } from "../../../components/icons";
import { getAvatarColor } from "../../../utils/getAvatarColor";

import {
  authSelectors,
  uiActions,
  useDispatch,
  useSelector,
} from "../../../state";

import { useStyles } from "./TopRightMenu.styles";
import LogoutIcon from "../../../components/icons/LogoutIcon";
import { NODE_ENV } from "../../../config";
import PersonalLink from "../../../components/icons/PersonalLinkIcon";
import { useMobile } from "../../../themes";

const TopRightMenu = () => {
  const classes = useStyles();
  const isMobile = useMobile();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const userFirstName = useSelector(authSelectors.userFirstName);
  const userLastName = useSelector(authSelectors.userLastName);
  const initials = useMemo(
    () =>
      `${userFirstName?.charAt(0) ?? ""}${
        userLastName?.charAt(0) ?? ""
      }`.toUpperCase(),
    [userFirstName, userLastName],
  );
  const avatarBg = useMemo(
    () => getAvatarColor(`${userFirstName} ${userLastName}`),
    [userFirstName, userLastName],
  );
  const agencyDomain = useSelector(authSelectors.domain);
  const referral_token = useSelector(authSelectors.referralToken);
  const affiliateToken = useSelector(authSelectors.affiliateToken);
  const domain =
    NODE_ENV === "development" ? window.location.host : agencyDomain;
  const activeRole = useSelector(authSelectors.activeRole);
  const notProductOwner = activeRole !== "referral_product_owner";
  const isAffiliateAgent = activeRole === "affiliate_agent";
  const salesDirectorRole =
    activeRole === "sales_director" || activeRole === "sales_admin";
  const dispatch = useDispatch();
  // Open when closed, close when open. On mobile the menu is tucked
  // behind the header (see `menu` in TopRightMenu.styles.ts) so tapping
  // the avatar a second time reaches the button rather than the
  // backdrop, and this handler is what dismisses the menu.
  //
  // On desktop the Menu's invisible backdrop intercepts the click first
  // (firing `onClose` → `setAnchorEl(null)`) before the avatar's own
  // click handler runs. We must NOT use a functional updater here:
  // by the time the updater runs, the backdrop has already queued
  // `null`, so `prev` reads as `null` and the avatar would re-open the
  // menu — producing a close-then-reopen flicker on every dismiss tap.
  // Branching on the closure-captured `anchorEl` instead reads the
  // pre-backdrop value (non-null) and queues another `null`, which
  // collapses harmlessly into the backdrop's update.
  const handleClick = useCallback(
    (event) => {
      if (anchorEl) {
        setAnchorEl(null);
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [anchorEl],
  );
  const urlParam = affiliateToken
    ? `affiliate_token=${affiliateToken}`
    : `referral_token=${referral_token}`;
  const SiteLink = () => {
    return (
      <LinkTab
        href={`https://${agencyDomain}?${urlParam}`}
        target="_blank"
        className={classes.link}
      >
        <span className={classes.iconWrapper}>
          <LaunchIcon />
        </span>{" "}
        View site
      </LinkTab>
    );
  };
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <div className={classes.topRight}>
      <Button
        className={classes.menuBtn}
        aria-controls="simple-menu"
        aria-haspopup="true"
        onClick={handleClick}
      >
        <Avatar className={classes.avatar} sx={{ backgroundColor: avatarBg }}>
          {initials}
        </Avatar>
      </Button>
      <Menu
        id="admin-menu"
        anchorEl={anchorEl}
        keepMounted
        // Don't lock body scroll while open — the menu has no scrollable
        // content of its own, and the default `Modal` scroll-lock briefly
        // hides the page scrollbar (replacing it with compensating
        // padding) which causes a visible horizontal "jump" of the
        // underlying page on every avatar tap.
        disableScrollLock
        open={Boolean(anchorEl)}
        className={classes.menu}
        onClose={handleClose}
      >
        {!isAffiliateAgent ? (
          <MenuItem
            onClick={handleClose}
            className={classes.link}
            component={Link}
            to="/admin/profile"
          >
            <span className={classes.iconWrapper}>
              <AccountCircleIcon />
            </span>{" "}
            Profile
          </MenuItem>
        ) : null}
        {notProductOwner && !salesDirectorRole && (
          <MenuItem className={classes.link}>
            <CopyToClipboard
              onCopy={() => {
                dispatch(uiActions.showSuccess("Link copied to clipboard"));
              }}
              text={`${domain}?${urlParam}`}
            >
              <div>
                <span className={classes.linkWrapper}>
                  <PersonalLink />
                </span>
                My personal link
              </div>
            </CopyToClipboard>
          </MenuItem>
        )}
        {isMobile ? (
          <MenuItem className={classes.link}>
            <SiteLink />
          </MenuItem>
        ) : null}
        <MenuItem
          onClick={handleClose}
          className={classes.link}
          component={Link}
          to="/auth/logout"
        >
          <span className={classes.iconWrapper}>
            <LogoutIcon />
          </span>{" "}
          Logout
        </MenuItem>
      </Menu>
    </div>
  );
};

export default TopRightMenu;
